import { FC, useEffect, useState } from "react"

import { css } from "@emotion/react"
import { Box, Button, ButtonProps, Divider, Heading, HeadingProps } from "@ncs/web-legos"
import { PartPreviewCardEcomm } from "."
import { useOnliePartsEcommService } from "@ncs/ncs-api/portal-apps"
import { Skeleton } from "@material-ui/lab"
import { CustomerPart, OnlinePartEcomm } from "@ncs/ncs-api"

interface PartPreviewRowProps {
	heading?: string
	partIds: (string | null | undefined)[]
	button?: ButtonProps
	headingVariant?: HeadingProps["variant"]
	onAddToCart?: (partId: string) => void
}

// Helper to map an OnlinePartEcomm or NonParentOnlinePart to a CustomerPart
export function mapOnlinePartToCustomerPart(part: OnlinePartEcomm): CustomerPart {
	const test: CustomerPart = {
		id: String(part.id),
		description: part.description ?? null,
		flatShippingRate: null,
		imageUrl: part.imageUrl ?? null,
		childImage: part.childImage ?? null,
		isActive: Boolean(part.isActive),
		isShippedFlatRate: false,
		netPrice: part.netPrice !== undefined ? Number(part.netPrice) : null,
		childNetPrice: part.childNetPrice ?? null,
		onlinePartNumber: part.onlinePartNumber ?? null,
		part: {
			id: String(part.partId),
			description: part.description,
			partNumber: part.partNumber,
			unitOfMeasure: part.unitOfMeasure,
		},
		price: part.price ?? null,
		shippingLeadDays: 0,
		title: part.title ?? null,
		isParent: Boolean(part.isParent),
		isChild: false,
		variantCount: part.variantCount ?? 0,
		brandName: part.brandName ?? null,
		featuredItem: false,
		clearanceItem: Boolean(part.clearanceItem),
		clearanceItemDiscount: part.clearanceItemDiscount ?? null,
		variants: [],
	}
	return test
}

export const PartPreviewRow: FC<PartPreviewRowProps> = ({
	heading,
	partIds,
	button,
	headingVariant = "h2",
	onAddToCart,
}) => {
	const partIdsToShow: string[] = [
		...new Set(partIds?.filter((id) => id).map((id) => String(id))),
	]

	const [customerParts, isLoadingCustomerParts, { query }] = useOnliePartsEcommService({
		params: {
			onlinePartIds: partIdsToShow,
		},
	})

	if (partIdsToShow.length === 0) {
		return null
	}

	return (
		<>
			{heading && (
				<Heading variant={headingVariant} bold mt={1} mb={1.5}>
					{heading}
				</Heading>
			)}

			<Box
				d="flex"
				css={css`
					padding-left: 1em;
					padding-top: 1em;
					padding-bottom: 1em;
					max-width: 100vw;
					overflow-x: auto;
					gap: 24px;
					&::-webkit-scrollbar {
						height: 4px;
					}

					&::-webkit-scrollbar-thumb {
						background-color: rgba(0, 0, 0, 0.2);
						border-radius: 2px;
					}

					&::-webkit-scrollbar-track {
						background: transparent;
					}
				`}
			>
				{isLoadingCustomerParts ?
					Array.from({ length: 5 }).map((_, index) => (
						<Box display="flex" flexDirection="column" key={index}>
							<Skeleton variant="rect" height={200} width={220} />
							<Box mt={3}>
								<Skeleton variant="text" height={44} width={220} />
							</Box>
							<Box>
								<Skeleton variant="text" height={22} width={120} />
							</Box>
							<Box mt={3}>
								<Skeleton variant="text" height={42} width={120} />
							</Box>
							<Box mt={3}>
								<Skeleton variant="text" height={"52px"} width={220} />
							</Box>
						</Box>
					))
				:	customerParts?.map((part) => (
						<Box key={part.id}>
							<PartPreviewCardEcomm
								part={mapOnlinePartToCustomerPart(part)}
								onAddToCart={onAddToCart}
							/>
						</Box>
					))
				}
			</Box>

			{button && (
				<Box mt={2}>
					<Button {...button} />
				</Box>
			)}
		</>
	)
}
